import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Card,
  InputNumber,
  Switch,
  Typography,
  Upload,
  message,
  Spin,
  Tag,
  Image,
  Progress,
} from "antd";
import { PlusOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { ArrowLeft, Camera } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../helper/apiClient";
import apiPath from "../../constants/apiPath";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ProductAddEdit = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [attributes, setAttributes] = useState([]); // State for active attributes
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const isUploadingRef = useRef(false);
  const messageKeyRef = useRef(null);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await apiClient.get(
        `${apiPath.activeCategoryList}`
      );

      if (response.data?.success) {
        const categoryList = response.data.categories || response.data.data || [];
        setCategories(categoryList.filter(cat => cat.status === true || cat.status === "true"));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      message.error("Failed to load categories");
    }
  };

  // Fetch active attributes from API
  const fetchActiveAttributes = async () => {
    try {
      const response = await apiClient.get(`${apiPath.activeAttribute}`);
      
      if (response.data?.success) {
        const attributeList = response.data.data || response.data.attributes || [];
        setAttributes(attributeList);
      } else {
        console.error("Failed to fetch attributes:", response.data);
      }
    } catch (error) {
      console.error("Error fetching attributes:", error);
      message.error("Failed to load attributes");
    }
  };

  // Upload multiple images
  const uploadMultipleImages = async (files) => {
    // Prevent duplicate uploads
    if (isUploadingRef.current) {
      console.log("Upload already in progress, ignoring...");
      return;
    }

    isUploadingRef.current = true;
    setUploadingImages(true);

    const newImages = [];
    let successCount = 0;
    let failCount = 0;

    // Show loading message
    messageKeyRef.current = message.loading(`Uploading ${files.length} image(s)...`, 0);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadKey = `${file.name}_${Date.now()}_${i}`;

      setUploadProgress(prev => ({ ...prev, [uploadKey]: 0 }));

      const formData = new FormData();
      formData.append("image", file);

      try {
        // Log the request details for debugging
        console.log("Uploading to:", `${apiPath.baseURL}${apiPath.imageUpload}`);

        const response = await apiClient.post(
          `${apiPath.imageUpload}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(prev => ({ ...prev, [uploadKey]: percentCompleted }));
            },
          }
        );

        console.log("Upload response:", response.data);

        // Check different response structures
        let imageUrl = null;
        if (response.data?.status === 200 && response.data?.data?.image_url) {
          imageUrl = response.data.data.image_url;
        } else if (response.data?.success && response.data?.image_url) {
          imageUrl = response.data.image_url;
        } else if (response.data?.url) {
          imageUrl = response.data.url;
        } else if (response.data?.image_url) {
          imageUrl = response.data.image_url;
        }

        if (imageUrl) {
          const isPrimary = uploadedImages.length === 0 && newImages.length === 0;
          const imageData = {
            uid: `${Date.now()}_${i}_${Math.random()}`,
            name: file.name,
            status: "done",
            url: imageUrl,
            image_url: imageUrl,
            alt_text: file.name,
            is_primary: isPrimary,
          };
          newImages.push(imageData);
          successCount++;
        } else {
          failCount++;
          console.error(`Failed to upload ${file.name}:`, response.data);
          message.error(`Failed to upload ${file.name}: ${response.data?.message || "Unknown error"}`);
        }
      } catch (error) {
        failCount++;
        console.error(`Error uploading ${file.name}:`, error);

        // Log detailed error information
        if (error.response) {
          console.error("Error response:", error.response.data);
          console.error("Error status:", error.response.status);

          // Handle 401 Unauthorized
          if (error.response.status === 401) {
            message.error("Authentication failed. Please login again.");
            // Optionally redirect to login
            // navigate("/login");
          } else {
            message.error(`Upload failed: ${error.response.data?.detail || error.response.data?.message || "Unknown error"}`);
          }
        } else {
          message.error(`Upload failed: ${error.message}`);
        }
      } finally {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[uploadKey];
          return newProgress;
        });
      }
    }

    // Close loading message
    if (messageKeyRef.current) {
      messageKeyRef.current();
    }

    // Show single summary message
    if (successCount > 0 && failCount === 0) {
      message.success(`✓ ${successCount} image(s) uploaded successfully`);
    } else if (successCount > 0 && failCount > 0) {
      message.warning(`Uploaded ${successCount} image(s), ${failCount} failed`);
    } else if (failCount > 0) {
      message.error(`Failed to upload ${failCount} image(s)`);
    }

    setUploadedImages(prevImages => [...prevImages, ...newImages]);
    setUploadingImages(false);
    isUploadingRef.current = false;
  };

  // Handle file selection - Simplified version
  const handleFileChange = (info) => {
    // Get the selected files
    const { fileList } = info;

    if (fileList && fileList.length > 0) {
      // Extract actual file objects
      const filesToUpload = fileList
        .filter(f => f.originFileObj)
        .map(f => f.originFileObj);

      if (filesToUpload.length > 0 && !isUploadingRef.current) {
        uploadMultipleImages(filesToUpload);
      }
    }
  };

  // Handle image removal
  const handleImageRemove = (file) => {
    setUploadedImages(uploadedImages.filter(img => img.uid !== file.uid));
    message.success("Image removed");
  };

  // Handle setting primary image
  const setPrimaryImage = (uid) => {
    setUploadedImages(uploadedImages.map(img => ({
      ...img,
      is_primary: img.uid === uid
    })));
    message.success("Primary image set");
  };

  // Fetch product data for edit mode
  const fetchProductData = async () => {
    if (!isEditMode) return;

    setLoading(true);
    try {
      const response = await apiClient.get(
        `${apiPath.marketPlaceProductDetail}${id}/`
      );

      if (response.data?.success) {
        const product = response.data.data || response.data.product;

        // Set form values
        form.setFieldsValue({
          name: product.name,
          sku: product.sku,
          description: product.description,
          price: parseFloat(product.price),
          stock_quantity: product.stock_quantity,
          category: product.category,
          featured: product.featured,
          bestseller: product.bestseller,
          limited_edition: product.limited_edition,
          status: product.status,
          manufacturer: product.manufacturer,
        });

        // Set uploaded images
        if (product.images && product.images.length > 0) {
          setUploadedImages(product.images.map(img => ({
            uid: img.id,
            name: img.alt_text || "image",
            status: "done",
            url: img.image_url,
            image_url: img.image_url,
            alt_text: img.alt_text,
            is_primary: img.is_primary,
          })));
        }

        // Set attributes with attribute_id
        if (product.attributes && product.attributes.length > 0) {
          form.setFieldsValue({
            attributes: product.attributes.map(attr => ({
              attribute_id: attr.id || attr.attribute_id,
              name: attr.name, // Keep for display
              value: attr.value
            }))
          });
        }
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      message.error("Failed to load product data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchActiveAttributes(); // Fetch active attributes
    if (isEditMode) {
      fetchProductData();
    }
  }, [id]);

  // Handle form submission
  const onFinish = async (values) => {
    if (uploadedImages.length === 0) {
      message.warning("Please upload at least one product image");
      return;
    }

    setSubmitting(true);

    // Transform attributes to include attribute_id
    const transformedAttributes = (values.attributes || []).map(attr => ({
      attribute_id: attr.attribute_id,
      value: attr.value
    }));

    // Prepare data for API
    const productData = {
      name: values.name,
      description: values.description,
      price: parseFloat(values.price),
      sku: values.sku,
      category: values.category,
      manufacturer: values.manufacturer || "",
      stock_quantity: values.stock_quantity || 1,
      featured: values.featured || false,
      status: values.status !== undefined ? values.status : true,
      bestseller: values.bestseller || false,
      limited_edition: values.limited_edition || false,
      specifications: values.specifications || {},
      images: uploadedImages.map(img => ({
        image_url: img.image_url || img.url,
        alt_text: img.alt_text || img.name,
        is_primary: img.is_primary || false,
      })),
      attributes: transformedAttributes,
    };

    try {
      let response;

      if (isEditMode) {
        // Update existing product
        response = await apiClient.put(
          `${apiPath.marketPlaceProductUpdate}${id}/`,
          productData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      } else {
        // Create new product
        response = await apiClient.post(
          `${apiPath.marketPlaceProductCreate}`,
          productData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
      }

      if (response.data?.success) {
        message.success(isEditMode ? "Product updated successfully" : "Product created successfully");
        navigate("/catalog/products");
      } else {
        message.error(response.data?.message || "Failed to save product");
      }
    } catch (error) {
      console.error("Error saving product:", error);
      message.error(error.response?.data?.message || "Failed to save product");
    } finally {
      setSubmitting(false);
    }
  };

  // Custom request handler that does nothing
  const customRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 100);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="product-add-edit-container">
      {/* Header */}
      <div className="header">
        <div className="left">
          <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(-1)} />
          <Title level={4} style={{ margin: 0 }}>
            {isEditMode ? "Edit Product" : "Add Product"}
          </Title>
        </div>

        <div className="right">
          <Button onClick={() => navigate(-1)}>Back</Button>
          <Button type="primary" onClick={() => form.submit()} loading={submitting}>
            {isEditMode ? "Update Product" : "Save Product"}
          </Button>
        </div>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{
        status: true,
        featured: false,
        bestseller: false,
        limited_edition: false,
        stock_quantity: 1,
      }}>
        <Row gutter={20}>
          {/* LEFT COLUMN */}
          <Col xs={24} xl={18}>
            {/* General Information */}
            <Card title="General" className="card">
              <Row gutter={16}>
                <Col span={24} md={12}>
                  <Form.Item name="sku" label="SKU" rules={[{ required: true, message: "Please enter SKU" }]}>
                    <Input placeholder="Enter product SKU" />
                  </Form.Item>
                </Col>
                <Col span={24} md={12}>
                  <Form.Item name="name" label="Product Name" rules={[{ required: true, message: "Please enter product name" }]}>
                    <Input placeholder="Enter product name" />
                  </Form.Item>
                </Col>
                <Col span={24} md={12}>
                  <Form.Item name="category" label="Category" rules={[{ required: true, message: "Please select category" }]}>
                    <Select placeholder="Select category" showSearch>
                      {categories.map(cat => (
                        <Option key={cat.id} value={cat.id}>
                          {cat.title || cat.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24} md={12}>
                  <Form.Item name="manufacturer" label="Brand">
                    <Input placeholder="Enter brand name" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="description" label="Description">
                    <TextArea rows={4} placeholder="Enter product description" />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Product Attributes */}
            <Card title="Product Attributes" className="card">
              <Form.List name="attributes">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <div key={field.key} style={{ marginBottom: 16 }}>
                        <Row gutter={16} align="middle">
                          <Col span={10}>
                            <Form.Item
                              {...field}
                              name={[field.name, "attribute_id"]}
                              fieldKey={[field.fieldKey, "attribute_id"]}
                              label={index === 0 ? "Attribute Name" : ""}
                              rules={[{ required: true, message: "Please select attribute" }]}
                            >
                              <Select 
                                placeholder="Select attribute"
                                showSearch
                                optionFilterProp="children"
                              >
                                {attributes.map(attr => (
                                  <Option key={attr.id} value={attr.id}>
                                    {attr.name}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={10}>
                            <Form.Item
                              {...field}
                              name={[field.name, "value"]}
                              fieldKey={[field.fieldKey, "value"]}
                              label={index === 0 ? "Value" : ""}
                              rules={[{ required: true, message: "Please enter value" }]}
                            >
                              <Input placeholder="Enter attribute value (e.g., Red, Large, Cotton)" />
                            </Form.Item>
                          </Col>
                          <Col span={4} style={{ textAlign: "center" }}>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => remove(field.name)}
                              style={{ marginTop: index === 0 ? 24 : 0 }}
                            />
                          </Col>
                        </Row>
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Attribute
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>

            {/* Product Images */}
            <Card title="Product Images" className="card">
              <div className="upload-header">
                <small>Image resolution should be at least 560px × 609px. You can select multiple images.</small>
              </div>

              <div className="image-gallery">
                {uploadedImages.map((img) => (
                  <div key={img.uid} className="image-item">
                    <Image
                      src={img.url}
                      alt={img.alt_text}
                      width={120}
                      height={120}
                      style={{ objectFit: "cover" }}
                      preview={false}
                    />
                    <div className="image-actions">
                      {!img.is_primary && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => setPrimaryImage(img.uid)}
                        >
                          Set Primary
                        </Button>
                      )}
                      {img.is_primary && (
                        <Tag color="green">Primary</Tag>
                      )}
                      <Button
                        size="small"
                        danger
                        onClick={() => handleImageRemove(img)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}

                <Upload
                  customRequest={customRequest}
                  onChange={handleFileChange}
                  showUploadList={false}
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple={true}
                  disabled={uploadingImages}
                >
                  <div className="upload-content-box">
                    {uploadingImages ? <LoadingOutlined spin /> : <Camera />}
                    <div className="upload-inner" style={{ marginTop: 8 }}>
                      {uploadingImages ? "Uploading..." : "Add Images"}
                    </div>
                    <div>png, jpeg, jpg</div>
                  </div>
                </Upload>
              </div>

              {/* Upload progress indicator */}
              {uploadingImages && Object.keys(uploadProgress).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Progress
                    percent={Math.floor((Object.keys(uploadProgress).filter(key => uploadProgress[key] === 100).length / Object.keys(uploadProgress).length) * 100)}
                    status="active"
                  />
                  <small>Uploading {Object.keys(uploadProgress).length} image(s)...</small>
                </div>
              )}
            </Card>
          </Col>

          {/* RIGHT COLUMN */}
          <Col xs={24} xl={6} className="sticky">
            {/* Points/Price */}
            <Card title="Points & Pricing" className="card">
              <Form.Item
                name="price"
                label="Points"
                rules={[{ required: true, message: "Please enter points/price" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  controls={false}
                  placeholder="Enter points"
                  stringMode
                  formatter={(value) =>
                    value
                      ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      : ""
                  }
                  parser={(value) => value.replace(/,/g, "")}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Card>

            {/* Settings */}
            <Card title="Settings" className="card">
              <div className="switch-row">
                <Text>Featured Product</Text>
                <Form.Item name="featured" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>

              <div className="switch-row">
                <Text>Limited Edition</Text>
                <Form.Item name="limited_edition" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>

              <div className="switch-row">
                <Text>Bestseller</Text>
                <Form.Item name="bestseller" valuePropName="checked" noStyle>
                  <Switch />
                </Form.Item>
              </div>

              <div className="switch-row">
                <Text>Active Status</Text>
                <Form.Item name="status" valuePropName="checked" noStyle>
                  <Switch defaultChecked />
                </Form.Item>
              </div>
            </Card>

            {/* Inventories */}
            <Card title="Inventory" className="card">
              <Form.Item
                name="stock_quantity"
                label="Stock Quantity"
                rules={[{ required: true, message: "Please enter stock quantity" }]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  placeholder="Enter stock quantity"
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>

      <style jsx>{`
        .product-add-edit-container {
          padding: 24px;
          background: #f0f2f5;
          min-height: 100vh;
        }
        
        .header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
        }
        
        .left {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        
        .right {
          display: flex;
          gap: 10px;
        }
        
        .card {
          margin-bottom: 16px;
          border-radius: 8px;
        }
        
        .sticky {
          position: sticky;
          top: 20px;
        }
        
        .switch-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        
        .upload-header {
          margin-bottom: 16px;
        }
        
        .image-gallery {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
        }
        
        .image-item {
          position: relative;
          width: 120px;
          border: 1px solid #d9d9d9;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .image-actions {
          padding: 8px;
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }
        
        .upload-content-box {
          width: 120px;
          height: 120px;
          border: 1px dashed #d9d9d9;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: #fafafa;
        }
        
        .upload-content-box:hover {
          border-color: #1890ff;
        }
        
        .upload-inner {
          font-size: 12px;
          color: #666;
        }
      `}</style>
    </div>
  );
};

export default ProductAddEdit;